import { useEffect, useState } from 'react';
import { Alert, AlertColor, Box, Card, CardContent, CircularProgress, FormControl, Grid, InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { useVForm } from 'shared/forms';

import { FerramentasDeDetalhe, InputError, ConfirmacaoModal } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { t } from 'i18next';
import { ICreateUsuario, IListagemLogsUsuPag, InicitalCreateUsuario, listKeyValueUsuarioStatus } from 'shared/types';
import { stringToDateTime } from 'shared/utils';
import { UsuariosService } from 'shared/services/usuarios/usuariosService';
import { useHasPermissionAcao } from 'shared/hooks';
import { LogService } from 'shared/services/usuarios/LogService';


const validationSchema = yup.object({
  UsuarioNome: yup.string().required(),
  NrCpf: yup.string(),
  UsuarioStatus: yup.string().required(),
  EMail: yup.string().email().required(),
  CodigoIdentificacaoFuncionario: yup.string().required(),
  Login: yup.string().required(),
  LoginAD: yup.string(),
  usu_Senha: yup.string(),
});

export const DetalheDeUsuario: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();

  const hasPermitionCreate = useHasPermissionAcao('usuario', 'CRE');
  const hasPermitionDelete = false;//useHasPermissionAcao('usuario','DEL');
  const hasPermitionUpdate = useHasPermissionAcao('usuario', 'UPD');

  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const { formRef, save, isSaveAndClose } = useVForm();

  const { register, handleSubmit, reset, watch, formState: { errors }, } = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onBlur'
  });

  const [rows, setRows] = useState<IListagemLogsUsuPag[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [stateSnack, setStateSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [titleConfirmacao, setTitleConfirmacao] = useState('');
  const [messageConfirmacao, setMessageConfirmacao] = useState('');


  const selectStatus = watch('UsuarioStatus');
  // const selectsenha = watch('usu_Senha');
  const dataUltimoLogin = stringToDateTime(watch('DataLoginUltimo'));

  useEffect(() => {
    setAlert(null);
    if (id !== 'novo') {
      setIsLoading(true);

      UsuariosService.getById(Number(id))
        .then((result) => {
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
            navigate('/usuario/listagem');
          } else {
            delete result.ROW_NUM;
            reset(result);
          }
          setIsLoading(false);
        });

      LogService.getAllLogsPag(Number(id))
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setStateSnack({ open: true, severity: 'error', message: `Ocorreu o seguinte erro: ${result.message}` });
          } else {
            setTotalCount(result.count);
            setRows(result.rows);

          }
        });

    }
    else {
      reset(InicitalCreateUsuario);
    }

  }, [id]);

  const handleDelete = () => {
    setTitleConfirmacao(t('components.actions.apagar'));
    setMessageConfirmacao(t('messages.usuario.exclusion') + id + '?');
    setOpenConfirmacao(true);
    //console.log('handleDelete');
  };

  const confirmDelete = () => {
    /*    setAlert(null);
    UsuariosService.deleteById(Number(id))
      .then(result => {
        if (result instanceof Error) {
          setAlert({
            text: result.message,
            type: 'error',
          });
          setOpenConfirmacao(false);
        } else {
          setAlert({
            text: 'Registro apagado com sucesso!',
            type: 'success',
          });
          navigate('/usuario/listagem');
        }
      })
      .catch( reason => {
        setAlert({
          text: reason,
          type: 'error',
        });
        setOpenConfirmacao(false);
      });
    */
  };

  const onSubmit = async (data: ICreateUsuario) => {

    setAlert(null);
    setIsSubmitting(true);
    if (id === 'novo') {
      UsuariosService
        .create(data)
        .then((result) => {
          setIsLoading(false);

          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/usuario/listagem');
            }
            setAlert({
              text: t('messages.success.incluido'),
              type: 'success',
            });
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    } else {
      UsuariosService
        .updateById(Number(id), data)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/usuario/listagem');
            }
            setAlert({
              text: t('messages.success.alterado'),
              type: 'success',
            });

          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    }

  };

  return (
    <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Usuario' : hasPermitionUpdate ? 'Alterar Usuário' : 'Consultar Usuário'}
      barraDeFerramentas={!(isLoading || isSubmitting) && (
        <FerramentasDeDetalhe
          idForm='formDetalheUsuario'
          textoBotaoNovo={t('components.actions.novo')}
          textoBotaoGenerico='Role'
          iconeBotaoGenerico='badge'
          textoBotaoGenerico2='Org'
          iconeBotaoGenerico2='store'

          mostrarBotaoNovo={id !== 'novo' && hasPermitionCreate}
          mostrarBotaoApagar={id !== 'novo' && hasPermitionDelete}
          mostrarBotaoSalvar={hasPermitionCreate || hasPermitionUpdate}
          mostrarBotaoGenerico={id !== 'novo'}
          mostrarBotaoGenerico2={id !== 'novo'}

          aoClicarEmSalvar={save}
          aoClicarEmVoltar={() => navigate('/usuario/listagem')}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/usuario/detalhe/novo')}
          aoClicarEmGenerico={() => navigate(`/usuario/detalhe/${id}/role`)}
          aoClicarEmGenerico2={() => navigate(`/usuario/detalhe/${id}/org`)}

        />)
      }
    >
      <Box ref={formRef} id='formDetalheUsuario' component="form" display="flex" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" width='100%' >

          <Card >
            <CardContent sx={{ padding: 0 }} >
              <div>
                {alert ? (
                  <>
                    <Grid item xs={12} paddingX={2}>
                      <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ marginTop: '1rem', marginBottom: '0' }}>
                        {alert.text}
                      </Alert>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} component={Typography} paddingX={2}>

                  </Grid>
                )}
                <Grid container  >
                  <Grid item xs={6} paddingX={2}>
                    <TextField
                      error={errors?.UsuarioNome?.type ? true : false}
                      fullWidth
                      id='UsuarioNome'
                      type='text'
                      label={`${t('pages.usuario.usuarioNome')} *`}
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('UsuarioNome')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.UsuarioNome?.type && <InputError type={errors.UsuarioNome.type} field='UsuarioNome' />}
                  </Grid>
                  <Grid item xs={3} paddingX={2}>
                    <TextField
                      error={errors?.nrCpf?.type ? true : false}
                      fullWidth
                      id='nrCpf'
                      type='number'
                      label={t('pages.usuario.nrCpf')}
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('nrCpf')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.nrCpf?.type && <InputError type={errors.nrCpf.type} field='nrCpf' />}
                  </Grid>

                  <Grid item xs={3} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <InputLabel id='label-select-status'>{`${t('pages.usuario.usuarioStatus')} *`}</InputLabel>
                      <Select
                        disabled={isSubmitting || isLoading }
                        error={errors?.UsuarioStatus?.type ? true : false}
                        labelId='label-select-canal'
                        id='UsuarioStatus'
                        input={<OutlinedInput label={`${t('pages.usuario.usuarioStatus')} *`} />}
                        {...register('UsuarioStatus')}
                        value={selectStatus || ''}
                      >
                        <MenuItem value=''>
                          <ListItemText primary='' />
                        </MenuItem>
                        {listKeyValueUsuarioStatus.map((item) => (
                          <MenuItem key={item.key} value={item.key} >
                            <ListItemText primary={item.value} sx={{ margin: '0' }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors?.UsuarioStatus?.type && <InputError type={errors.UsuarioStatus.type} field='UsuarioStatus' />}
                  </Grid>
                  <Grid item xs={3} paddingX={2}>
                    <TextField
                      error={errors?.EMail?.type ? true : false}
                      fullWidth
                      id='EMail'
                      type='text'
                      label={`${t('pages.usuario.eMail')} *`}
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('EMail')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.EMail?.type && <InputError type={errors.EMail.type} field='EMail' />}
                  </Grid>
                  <Grid item xs={3} paddingX={2}>
                    <TextField
                      error={errors?.CodigoIdentificacaoFuncionario?.type ? true : false}
                      fullWidth
                      id='CodigoIdentificacaoFuncionario'
                      type='text'
                      label={`${t('pages.usuario.codigoIdentificacaoFuncionario')} *`}
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('CodigoIdentificacaoFuncionario')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.CodigoIdentificacaoFuncionario?.type && <InputError type={errors.CodigoIdentificacaoFuncionario.type} field='CodigoIdentificacaoFuncionario' />}
                  </Grid>
                  <Grid item xs={3} paddingX={2}>
                    <TextField
                      error={errors?.Login?.type ? true : false}
                      fullWidth
                      id='Login'
                      type='text'
                      label={`${t('pages.usuario.login')} *`}
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('Login')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.Login?.type && <InputError type={errors.Login.type} field='Login' />}
                  </Grid>
                  <Grid item xs={3} paddingX={2}>
                    <TextField
                      error={errors?.LoginAD?.type ? true : false}
                      fullWidth
                      id='LoginAD'
                      type='text'
                      label={t('pages.usuario.loginAD')}
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('LoginAD')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.LoginAD?.type && <InputError type={errors.LoginAD.type} field='LoginAD' />}
                  </Grid>
                  {id === 'novo' && (<Grid item xs={4} paddingX={2}>
                    <TextField
                      error={errors?.usu_Senha?.type ? true : false}
                      fullWidth
                      id='usu_Senha'
                      type='password'
                      label={t('pages.usuario.senhaProvisoria')}
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('usu_Senha')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.usu_Senha?.type && <InputError type={errors.usu_Senha.type} field='usu_Senha' />}
                  </Grid>)}
                  {/* {id !== 'novo' && (
                    <>
                      <Grid item xs={3} paddingX={2}>
                        <TextField
                          fullWidth
                          id='DataLoginUltimo'
                          type='text'
                          label={t('pages.usuario.dtUltinoLogin')}
                          disabled={true}
                          value={dataUltimoLogin}
                          sx={{ marginTop: '1rem', marginBottom: '0' }}
                        />
                      </Grid>
                    </>

                  )} */}



                </Grid>
              </div>
            </CardContent>
          </Card>
        </Box>
        <ConfirmacaoModal
          open={openConfirmacao}
          title={titleConfirmacao}
          message={messageConfirmacao}
          onClose={() => setOpenConfirmacao(false)}
          onConfirm={() => confirmDelete()}
        />
      </Box>

      {
        id !== 'novo' &&  rows !== null?
          <TableContainer sx={{ maxWidth: 350 }} component={Paper}>
            <Table sx={{ maxWidth: 350 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('pages.usuario.usuarioStatus')}</TableCell>
                  <TableCell>{t('pages.usuario.usuarioAcao')}</TableCell>
                  <TableCell>{t('pages.usuario.dataHora')}</TableCell>
                </TableRow>
              </TableHead>
              {!isLoading && (<TableBody>
                {rows.map((row) => (
                  <TableRow key={row.UsuarioId}>
                    <TableCell>{row.USU_ST}</TableCell>                  
                    <TableCell>{row.USU_NM_INS}</TableCell>
                    <TableCell>{stringToDateTime(row.DhLog.toString())}</TableCell>
                  </TableRow>
                ))}
              </TableBody>)}
            </Table>
          </TableContainer>
          :
          ''
      }
      {(isLoading || isSubmitting) && (<Box sx={{
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <CircularProgress />
      </Box>)}

      
    </LayoutBaseDePagina >
  );
};
